// @flow
import * as React from 'react';
import styled, {
  type StyledComponent,
  type PropsWithTheme,
} from 'styled-components';
import memoize from 'lodash/fp/memoize';

import {
  fontSize,
  fontColor,
  fontWeight,
  fontItalic,
  fontDecoration,
  fontSmooth,
} from 'ui/styles';
// types
import type { FontSize, FontWeight, Color, Theme } from 'ui/styles';

export type TextAlign = 'right' | 'left' | 'center' | 'justify';
export type ElementTag =
  | 'a'
  | 'span'
  | 'p'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'div';

export type Props = $ReadOnly<{
  children?: React.Node,
  size?: FontSize,
  fSize?: FontSize,
  weight?: FontWeight,
  color?: Color,
  fColor?: Color,
  className?: string,
  marginBottom?: string,
  marginRight?: string,
  marginLeft?: string,
  is?: ElementTag,
  italic?: boolean,
  underline?: boolean,
  strikethrough?: boolean,
  cursor?: string,
  ...
}>;

type InnerProps = $ReadOnly<{|
  children?: React.Node,
  fSize: FontSize,
  weight?: FontWeight,
  fColor: Color,
  className?: string,
  marginBottom?: string,
  marginRight?: string,
  marginLeft?: string,
  italic?: boolean,
  underline?: boolean,
  strikethrough?: boolean,
  cursor?: string,
|}>;

/* 🤔 doing things like this might be a poor use of JS resources
      Might want to just use classes. Further thought or perf tests are needed.
 */
const styledElement: (ElementTag) => StyledComponent<
  InnerProps,
  Theme,
  HTMLElement,
> = memoize(
  (is) => styled[is]`
    margin: 0;
    margin-bottom: ${({ marginBottom }: PropsWithTheme<InnerProps, Theme>) =>
      marginBottom || '0'};
    margin-right: ${({ marginRight }: PropsWithTheme<InnerProps, Theme>) =>
      marginRight || '0'};
    margin-left: ${({ marginLeft }: PropsWithTheme<InnerProps, Theme>) =>
      marginLeft || '0'};
    ${fontItalic};
    ${fontWeight};
    ${fontColor};
    ${fontSize};
    ${fontSmooth};
    ${fontDecoration};
    ${({ cursor }: PropsWithTheme<InnerProps, Theme>) =>
      cursor ? `cursor: ${cursor};` : ''}
  `,
);

const Text = (
  {
    size,
    fSize,
    weight,
    italic,
    children,
    color,
    fColor,
    className,
    is = 'span',
    marginBottom = '0',
    marginRight = '0',
    marginLeft = '0',
    underline,
    ...rest
  }: Props,
  forwardedRef: React.Ref<any>,
) => {
  const Element = styledElement(is);
  return (
    /* 💁 using prop names like `fSize` instead of just `size` to avoid these props
        existing on the rendered DOM nodes:

        Any prop from this list 👇 will render to DOM as an attribute.
        https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes
  */
    <Element
      {...rest}
      ref={forwardedRef}
      fSize={size || fSize || 2}
      fColor={color || fColor || 'black'}
      weight={weight}
      italic={italic}
      underline={underline}
      className={className}
      marginBottom={marginBottom}
      marginRight={marginRight}
      marginLeft={marginLeft}
    >
      {children}
    </Element>
  );
};

export default (React.forwardRef<React.Config<Props, void>, HTMLElement>(
  Text,
): React$AbstractComponent<React.Config<Props, void>, HTMLElement>);
